import { useState, useEffect, useRef } from "react";
import { flushSync } from "react-dom";
import {
  ChevronDown,
  TrendingUp,
  TrendingDown,
  Share2,
  Download,
  Calendar,
  Info,
  MoreVertical,
} from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { SingleDatePicker } from "@/components/shared/SingleDatePicker";
import { AISparkles } from "@/components/ui/ai-sparkles";
import { SignalAIAvatar } from "@/components/icons/SignalAIAvatar";
import { toast } from "sonner";
import type { RankTrackerReport } from "@/types";
import { rankTrackerApi } from "@/resources/rank-tracker";
import { useAppData } from "@/contexts/AppDataContext";
import { ShareLinkModal } from "@/components/shared/ShareLinkModal";
import { ValidationPanel } from "@/components/ui/ValidationPanel";
import { KeywordRow } from "./KeywordRow";
import { exportReportToPdf } from "./exportPdf";

interface SignalAIRankReportProps {
  publicData?: RankTrackerReport;
}

export function SignalAIRankReport({ publicData }: SignalAIRankReportProps = {}) {
  const isPublic = !!publicData;
  const { selectedBrandId, brands } = useAppData();
  const { data: reports = [], isLoading } = rankTrackerApi.useGetRankTrackerReports(isPublic ? null : selectedBrandId);

  const [selectedReportId, setSelectedReportId] = useState<number | undefined>(reports[0]?.id);
  const [expanded, setExpanded] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);
  const prevLatestIdRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (isPublic) return;
    if (reports.length === 0) return;

    const latestId = reports[0].id;
    const ids = reports.map((r) => r.id);

    if (
      selectedReportId === undefined ||
      !ids.includes(selectedReportId) ||
      (prevLatestIdRef.current !== undefined && prevLatestIdRef.current !== latestId)
    ) {
      setSelectedReportId(latestId);
    }

    prevLatestIdRef.current = latestId;
  }, [reports, selectedReportId, isPublic]);

  if (!isPublic && isLoading) return null;
  if (!isPublic && reports.length === 0) return null;

  const report = isPublic ? publicData : (reports.find((r) => r.id === selectedReportId) ?? reports[0]);

  if (!report) return null;

  const topGains = report.gains.slice(0, 5);
  const topDeclines = report.declines.slice(0, 5);
  const generatedDate = format(new Date(report.createdAt), "d MMMM, yyyy");

  const handleExport = async () => {
    if (!reportRef.current) {
      toast.error("Report not ready, try again");
      return;
    }
    const wasExpanded = expanded;
    flushSync(() => setExpanded(true));
    setIsExporting(true);
    const currentBrand = brands.find((b) => b.id === selectedBrandId);
    const brandSlug = (currentBrand?.name ?? "brand")
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
    try {
      await exportReportToPdf(reportRef.current, brandSlug);
    } catch (error) {
      toast.error(`Export failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsExporting(false);
      if (!wasExpanded) setExpanded(false);
    }
  };

  return (
    <>
      <TooltipProvider delayDuration={200}>
        <Card ref={reportRef} variant="ai-plain">
          <div className="px-5 py-4 flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <SignalAIAvatar size="lg" className="shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-xl font-semibold text-foreground">
                    Signal AI Rank Report
                  </h3>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3.5 w-3.5 text-muted-foreground/60 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs leading-relaxed">
                      <p>A monthly AI-generated analysis of keyword ranking movements across your brand's product portfolio. Rankings reflect average organic position across all tracked ASINs.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  <span>{report.label}</span>
                  <span className="text-border">·</span>
                  <span>Generated {generatedDate}</span>
                  {report.source === "scheduled" && (
                    <>
                      <span className="text-border">·</span>
                      <span>Monthly</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {!isPublic && (
              <div data-html2canvas-ignore="true" className="flex items-center gap-2 shrink-0">
                {/* AUT-356: report versions are dates, so this is the canonical single-date
                    selector's options variation rather than a bespoke Select. */}
                <SingleDatePicker
                  options={reports.map((r) => ({
                    id: String(r.id),
                    label: format(new Date(r.createdAt), "d MMMM, yyyy"),
                  }))}
                  value={String(selectedReportId)}
                  onValueChange={(v) => setSelectedReportId(Number(v))}
                  className="w-[200px] bg-card"
                />

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="w-8">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setShareOpen(true)}>
                      <Share2 className="h-4 w-4 mr-2" />
                      Share Report
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleExport} disabled={isExporting}>
                      <Download className="h-4 w-4 mr-2" />
                      {isExporting ? "Exporting..." : "Export PDF"}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>

          <div className="px-5 pb-3">
            <p className="text-sm text-foreground leading-relaxed">
              {report.summaryShort}
            </p>
          </div>

          <Collapsible open={expanded} onOpenChange={setExpanded}>
            <div className="mx-5 pb-3">
              <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors w-full">
                <AISparkles className="h-4 w-4" />
                <span>{expanded ? "Read less" : "Read more"}</span>
                <ChevronDown className={`h-4 w-4 ml-auto transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
              </CollapsibleTrigger>
            </div>

            <CollapsibleContent>
              <div className="mx-5 border-t border-border pt-4 pb-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center gap-1.5 mb-1">
                      <TrendingUp className="h-3.5 w-3.5 text-foreground" />
                      <span className="text-xs font-semibold text-foreground">Biggest Gains</span>
                    </div>
                    <div className="divide-y divide-border">
                      {topGains.map((kw) => (
                        <KeywordRow key={kw.keyword} kw={kw} type="gain" />
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 mb-1">
                      <TrendingDown className="h-3.5 w-3.5 text-foreground" />
                      <span className="text-xs font-semibold text-foreground">Notable Declines</span>
                    </div>
                    <div className="divide-y divide-border">
                      {topDeclines.map((kw) => (
                        <KeywordRow key={kw.keyword} kw={kw} type="decline" />
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-1">Overall Trend Analysis</h3>
                  <p className="text-sm leading-relaxed text-foreground">{report.summaryFull}</p>
                </div>

                <p className="text-xs text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">Important context:</strong>{" "}
                  {report.contextNote}
                </p>

                <ValidationPanel validation={report.validation} className="px-2" />
              </div>
            </CollapsibleContent>
          </Collapsible>
        </Card>
      </TooltipProvider>
      {!isPublic && (
        <ShareLinkModal
          open={shareOpen}
          onOpenChange={setShareOpen}
          shareableType="rank_tracker_ai_report"
          frozenPayload={{
            id: report.id,
            brandId: report.brandId,
            reportMonth: report.reportMonth,
            label: report.label,
            gains: report.gains,
            declines: report.declines,
            summaryShort: report.summaryShort,
            summaryFull: report.summaryFull,
            contextNote: report.contextNote,
            source: report.source,
            createdAt: report.createdAt,
          }}
          resourceName="Signal AI Rank Report"
        />
      )}
    </>
  );
}
